import { useTranslation } from "react-i18next";
import {
  Box,
  Text,
  Flex,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  Image,
  SimpleGrid,
} from "@chakra-ui/react";
import { FormProvider } from "react-hook-form";
import imgPlaceholder from "@root/assets/icons/img-placeholder.svg";
import defaultThumbnail from "@root/assets/images/Course Thunbnail.png";
import { PlusIcon } from "@root/assets/icons/PlusIcon";
import CommonInputField from "@root/components/common/form/CommonInputField";
import CommonSelectAdvancedField from "@root/components/common/form/CommonSelectFieldAdvanced";
import CommonSelectField from "@root/components/common/form/CommonSelectField";
import CommonMultiSelectField from "@root/components/common/form/CommonMultiSelectField";
import CommonRadioField from "@root/components/common/form/CommonRadioField"; // Add this import
import { useCourseManagementContext } from "@root/context/course-management-context";
import { useEffect } from "react";
import {
  requirementOptions,
  SUBSCRIBER_TYPE,
  userTypeOptions,
} from "@root/constants";
import { useGlobalContext } from "@root/context/global-context";
import { useOrganizationBranding } from "@root/context/organization-branding-context";
import { useParams } from "react-router-dom";
import { title } from "process";

const CourseStep1 = ({ defaultValues }: { defaultValues?: any }) => {
  const { t } = useTranslation("common");
  const {
    methods1,
    register1,
    handleSubmit1,
    control1,
    fetchCategories,
    categoriesData,
    setSelectedCategory: setCategory,
    handleThumbnailSelect,
    selectedThumbnail,
    courseData,
  } = useCourseManagementContext();
  const { id } = useParams();
  const { setValue, watch, reset } = methods1;

  const { fetchOrganizations, organizationOptions } = useGlobalContext();
  const { slug } = useOrganizationBranding();
  const organizations = organizationOptions
    ?.filter((org: any) => org?.companyName)
    .map((org: any) => ({
      value: org?.id,
      label: org?.companyName,
    }));
  const courseSubscriberType = watch("courseSubscriberType");

  const categoriesOptions = categoriesData?.rows?.map((item: any) => ({
    label: item?.title,
    value: item?.id,
    children: item?.children?.map((subcat: any) => ({
      label: subcat?.title,
      value: subcat?.id,
    })),
  }));

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      // Edit mode - only reset if we have defaultValues
      reset(defaultValues);
    }
    // Don't reset for create mode - let the context handle it
  }, [defaultValues, id]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (courseSubscriberType && slug) {
      fetchOrganizations({
        role:
          Number(courseSubscriberType) === 1
            ? SUBSCRIBER_TYPE.SUBSCRIBER
            : SUBSCRIBER_TYPE.NON_SUBSCRIBER,
        slug,
      });
    }
  }, [courseSubscriberType, slug]);

  useEffect(() => {
    if (id && courseData) {
      setValue("title", courseData?.title);
      setValue("description", courseData?.description);
      setValue("category", courseData?.subcategory?.id);
      setValue("requirement", courseData?.requirement?.toString());
      setValue(
        "courseSubscriberType",
        courseData?.courseSubscriberType?.toString(),
      );

      setCategory([courseData?.category?.id, courseData?.subcategory?.id]);
    }
  }, [courseData, id]);

  useEffect(() => {
    if (
      id &&
      courseData?.courseManagers?.length > 0 &&
      organizations?.length > 0
    ) {
      const orgMembershipIds = courseData.courseManagers
        .map((manager: any) => manager.organizationMembership?.id)
        .filter((id: any) => id != null);

      const defaultSelectedOrgs = organizations
        .filter((org: any) => orgMembershipIds.includes(org.value))
        .map((org: any) => org.value);

      if (defaultSelectedOrgs.length > 0) {
        setValue("companiesIds", defaultSelectedOrgs);
      }
    }
  }, [courseData, organizations, id, setValue, courseSubscriberType]);

  const onSubmit = (data: FormValuesCourse) => {};

  const onChangeCustom = (value: any) => {
    setCategory(value?.split("-"));
  };

  return (
    <Box>
      <Text fontWeight={600} color={"#2F2F34"} fontSize={"24px"} mb={"10px"}>
        {t("courseCreate.title")}
      </Text>
      <Text color={"#798692"} mb={8}>
        {t("courseCreate.description")}
      </Text>
      <FormProvider {...methods1}>
        <form onSubmit={handleSubmit1(onSubmit)}>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <Input
                type="file"
                accept="image/*"
                display={"none"}
                {...register1("thumbnail")}
                onChange={handleThumbnailSelect}
              />
              <FormLabel
                fontWeight={500}
                fontSize={"14px"}
                color={"#1819B4"}
                mb={0}
                cursor={"pointer"}
              >
                <Box position="relative">
                  {selectedThumbnail ? (
                    <Image
                      src={URL.createObjectURL(selectedThumbnail)}
                      alt="Course Thumbnail Preview"
                      mt={2}
                      boxSize="120px"
                      objectFit="cover"
                      borderRadius="md"
                      border="1px solid #E2E8F0"
                    />
                  ) : (
                    <Image
                      src={
                        id && courseData?.thumbnail
                          ? courseData?.thumbnail
                          : defaultThumbnail
                      }
                      alt="Course Thumbnail Placeholder"
                      mt={2}
                      boxSize="120px"
                      objectFit="cover"
                      borderRadius="md"
                      border="1px solid #E2E8F0"
                      opacity={0.7}
                    />
                  )}
                </Box>
                <Flex gap={2} alignItems={"center"} mt={2}>
                  <PlusIcon />
                  {selectedThumbnail
                    ? t("courseCreate.editThumbnail")
                    : t("courseCreate.addCourseThumbnail")}
                </Flex>
              </FormLabel>
              <FormErrorMessage>
                {methods1?.formState?.errors?.thumbnail?.message ??
                  t("courseCreate.thumbnailFieldError")}
              </FormErrorMessage>
            </FormControl>

            <CommonInputField
              name="title"
              control={control1}
              label={t("courseCreate.courseTitle")}
              placeholder={t("courseCreate.courseTitlePlaceholder")}
              rules={{ required: t("courseCreate.courseRequired") }}
            />

            <CommonInputField
              name="description"
              control={control1}
              label={t("courseCreate.courseDescription")}
              placeholder={t("courseCreate.courseDescriptionPlaceholder")}
              rules={{ required: t("courseCreate.courseDescRequired") }}
              type="textarea"
              rows={7}
            />

            <SimpleGrid columns={2} spacingX={10}>
              <CommonSelectAdvancedField
                name="category"
                control={control1}
                label={t("courseCreate.category")}
                categories={categoriesOptions}
                placeholder={t("courseCreate.categoryPlaceholder")}
                onChangeCustom={onChangeCustom}
              />

              <CommonSelectField
                name="requirement"
                control={control1}
                label={t("courseCreate.courseRequirement")}
                options={requirementOptions()}
                placeholder={t("courseCreate.courseRequirementPlaceholder")}
                placement="top"
              />
            </SimpleGrid>

            <SimpleGrid columns={2} spacingX={10}>
              <CommonRadioField
                name="courseSubscriberType"
                control={control1}
                label={t("courseCreate.belongToOrg")}
                options={userTypeOptions()}
                orientation="horizontal"
                spacing={12}
                rules={{
                  required: t("courseCreate.pleaseSelect"),
                }}
              />
              <CommonMultiSelectField
                name="companiesIds"
                control={control1}
                label={t("courseCreate.affiliatedOrg")}
                options={organizations}
                placeholder={t("courseCreate.selectedOrg")}
                defaultValue={
                  courseData?.courseManagers?.length > 0
                    ? courseData.courseManagers
                        .filter((manager: any) => manager.organizationMembership?.id && manager.organizationMembership?.companyName)
                        .map((manager: any) => manager.organizationMembership?.id)
                    : []
                }
                // rules={{
                //   required: t("courseCreate.requiredAffiliatedOrg"),
                //   validate: (value: any[]) =>
                //     value.length > 0 || t("courseCreate.requiredAffiliatedOrg"),
                // }}
                maxHeight="250px"
              />
            </SimpleGrid>
          </VStack>
        </form>
      </FormProvider>
    </Box>
  );
};

export default CourseStep1;
